// Third-party libraries
import React, { useEffect, useContext, useState, useRef } from "react";
import { Link as DomLink } from "react-router-dom";
import { isAxiosError } from "axios";
import toast from "react-hot-toast";
import parse from "html-react-parser";
import InfoIcon from "@mui/icons-material/Info";
import ReactMarkdown from "react-markdown";
import {
  Select,
  Container,
  Typography,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Grid,
  Card,
  Stack,
  Backdrop,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  MenuItem,
  InputLabel,
} from "@mui/material";

// Local modules
import { CartContext } from "../../utils/stateHandlers/contexts";
import { api, storage, constants } from "../../utils";
import { functions } from "../../utils";
import formatting from "../../utils/formatHelpers";
import { Mobile, Default } from "../../utils/deviceHelpers";
import gaFunctions from "../../utils/GAComponents/GAFunctions";

const iFrameBoxStyles = {
  maxHeight: "50px",
};
const inputBoxStyles = {
  width: "100%",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  padding: "10px 14px",
  boxSizing: "border-box",
};

const inputLabelStyles = {
  fontSize: "1.2rem",
};

export default function Checkout(props) {
  const { cartState, cartDispatch } = useContext(CartContext);
  const {
    cart,
    fields,
    order,
    totals,
    submitted,
    shippingAddress,
    placedAt,
    pageLoaded,
    newAddress,
    newCreditCard,
    specialInstructions,
  } = cartState;
  const [openNewAddressModal, setOpenNewAddressModal] = useState(false);
  const [openNewCardModal, setOpenNewCardModal] = useState(false);
  const [shipTo, setShipTo] = useState("");
  const [shipVia, setShipVia] = useState("");
  const [showShippingPrice, setShowShippingPrice] = useState(true);
  const [customerNo, setCustomerNo] = useState("");
  const [isCheckoutValid, setIsCheckoutValid] = useState(false);

  const cartId =
    storage.getStorageItem("cartId") !== "null"
      ? storage.getStorageItem("cartId")
      : JSON.parse(storage.getStorageItem("cartId"));
  const currentUser = JSON.parse(storage.getStorageItem("currentUser"));
  const isFirstRender = useRef(true);

  function validateCheckoutFields() {
    if (order != null) {
      if (!order.ShipTo || !order.ShipVia || !order.TermsCode) {
        return false;
      }
      return true;
    }
    return false;
  }

  const handleShipViaChange = (e, field) => {
    const value = e.target.value;
    setShipVia(value);
    cartDispatch({
      type: "updateFields",
      field: field.name,
      payload: value,
    });
  };

  const handleTermsChange = (e) => {
    cartDispatch({
      type: "setNewCreditCard",
      field: "Name",
      payload: e.target.value,
    });
  };
  const handleSelectChange = (e, field) => {
    setShipTo(e.target.value);
    cartDispatch({
      type: "updateFields",
      field: field.name,
      payload: e.target.value,
    });
    if (field.name === "ShipTo") {
      cartDispatch({
        type: "updateFields",
        field: "ShipVia",
        payload: null,
      });
    }
  };
  const SelectField = (field) => {
    return (
      <Stack>
        <div>
          <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
            {field.label} *
            {field.name === "ShipTo" && (
              <span
                onClick={() => {
                  setOpenNewAddressModal(true);
                }}
                className="header-link pointer"
                style={{ marginBottom: ".3rem", padding: "unset" }}
              >
                Add New Address
              </span>
            )}
            {field.name === "TermsCode" && (
              <span
                onClick={() => setOpenNewCardModal(true)}
                className="header-link pointer"
                style={{ marginBottom: ".3rem", padding: "unset" }}
              >
                Add New Credit Card
              </span>
            )}
          </Typography>
        </div>
        <Select
          key={field.name}
          defaultValue={field.default}
          size="small"
          onChange={(e) => {
            handleSelectChange(e, field);
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                border: "1px solid #939598",
                borderRadius: "0",
              },
            },
          }}
        >
          {stripSelectLabels(field).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  };

  const InputField = (field) => (
    <Stack>
      <Typography
        sx={{
          fontWeight: "700",
          paddingBottom: ".3rem",
        }}
      >
        {field.label}
      </Typography>
      <TextField
        color="primary"
        onChange={(e) => {
          cartDispatch({
            type: "updateFields",
            field: field.name,
            payload: e.target.value,
          });
        }}
        size="small"
        sx={{
          width: "100%",
          margin: "unset",
        }}
      />
    </Stack>
  );

  const ShipViaField = (field) => {
    return (
      <FormControl>
        <FormLabel>
          <Typography
            sx={{ fontWeight: "700", paddingBottom: ".3rem", color: "black" }}
          >
            {field.label} *
          </Typography>
        </FormLabel>
        <RadioGroup
          value={order.ShipVia}
          sx={{ marginLeft: "2rem" }}
          onChange={(e) => handleShipViaChange(e, field)}
        >
          {field.values &&
            field.values.map((shipOption) => (
              <FormControlLabel
                key={shipOption.desc}
                value={JSON.stringify({
                  amount: shipOption.amount,
                  code: shipOption.code,
                  desc: shipOption.desc,
                })}
                control={<Radio disabled={!showShippingPrice} />}
                label={
                  <Typography>
                    {shipOption.desc} |{" "}
                    {formatting["price2"](shipOption.amount)}
                  </Typography>
                }
              />
            ))}
        </RadioGroup>
      </FormControl>
    );
  };

  const SpecialInstructions = (field) => (
    <Stack>
      <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
        Special Instructions
      </Typography>
      <TextField
        color="primary"
        variant="outlined"
        multiline
        fullWidth
        rows={3}
        sx={{
          margin: "unset",
        }}
        onChange={(e) => {
          cartDispatch({
            type: "setSpecialInstructions",
            payload: e.target.value,
          });
        }}
      />
    </Stack>
  );

  function stripSelectLabels(field) {
    return field.values.map((option) => {
      option.label = option.label.replace(" <br />", " | ");
      option.label = option.label.replace(" <br />", " | ");
      return option;
    });
  }

  async function checkout() {
    try {
      const shippingAddressField = fields.find(
        (field) => field.name === "ShipTo"
      );

      const selectedAddress = shippingAddressField?.values.find(
        (address) => address.value === order.ShipTo
      );

      cartDispatch({
        type: "setShippingAddress",
        payload: selectedAddress?.label || "",
      });
      const shipVia = JSON.parse(order.ShipVia);
      const orderInfo = {
        cart_id: cartId,
        MultipleOk: order.MultipleOk ? "Y" : "",
        Notes: specialInstructions,
        PoNumber: order.PoNumber,
        ShipTo: order.ShipTo,
        ShipVia: `${shipVia.desc} | ${shipVia.amount}`,
        TermsCode: order.TermsCode,
      };
      cartDispatch({ type: "setPageLoaded", payload: false });

      const response = await api.postRequest("orders/submit", orderInfo);

      cartDispatch({
        type: "setOrderSubmitted",
        payload: {
          placedAt: response.data.PlacedAt,
          submitted: true,
        },
      });
      cartDispatch({ type: "setPageLoaded", payload: true });
      cartDispatch({ type: "setCart", payload: [] });
      cartDispatch({ type: "setCartCount", payload: 0 });

      const gaPurchaseInfo = {
        items: cart,
        transaction_id: cartId,
        tax: totals.tax.amount,
        shipping: totals.trans,
        total: totals.total,
      };
      gaFunctions.gaPurchase(gaPurchaseInfo);
    } catch (error) {
      cartDispatch({ type: "setPageLoaded", payload: true });

      if (isAxiosError(error)) {
        const status = error.response?.status;
        const data = error.response?.data;

        if (status === 400 && data?.errors) {
          const messages = Object.values(data.errors).join(", ");
          toast.error(
            `There was an error processing your request. ${messages}`
          );
        } else {
          toast.error(
            `There was an error processing your request. ${
              data?.message || error.message
            }`
          );
        }
      } else {
        toast.error(`Unexpected error: ${error.message || error}`);
      }

      console.error("Checkout error:", error);
    }
  }

  function sortFields(fields) {
    const sortedFields = fields.map((field) => ({
      ...field,
      SortOrder:
        field.name === "ShipTo"
          ? 1
          : field.name === "ShipVia"
          ? 2
          : field.name === "TermsCode"
          ? 3
          : field.name === "MultipleOk"
          ? 4
          : field.name === "PoNumber"
          ? 5
          : 6,
    }));
    return sortedFields.sort((a, b) => a.SortOrder - b.SortOrder);
  }

  function updateFields(newFields) {
    const oldFields = fields.filter((field) =>
      ["MultipleOk", "PoNumber", "Notes"].includes(field.name)
    );
    const updatedFields = [oldFields];
    newFields.forEach(function (field) {
      if (field.name === "ShipTo") {
        updatedFields.push(field);
      } else if (field.name === "ShipVia") {
        updatedFields.push(field);
      } else if (field.name === "TermsCode") {
        updatedFields.push(field);
      } else return;
    });
    const flattenedFields = [].concat(...updatedFields);
    const sortedFields = sortFields(flattenedFields);
    return sortedFields;
  }

  async function reloadFields() {
    setShowShippingPrice(false);
    const body = {
      cart_id: cartId,
      ShipTo: order.ShipTo,
      TermsCode: order.TermsCode,
      ShipVia: JSON.parse(order.ShipVia),
    };
    try {
      const response = await api.postRequest("/orders/form", body);
      if (response.isAxiosError) return;

      const baseFields = response.data.fields;
      const totals = response.data.totals;
      const updatedFieldsStep1 = updateFields(baseFields);

      setCustomerNo(response.data.customerNo);
      const customerData = {
        customer: { customerNo: currentUser.CustomerNo },
      };
      const cards = await handleGetPayments(customerData);
      const updatedFieldsWithTerms = updateTermsCodeValues(
        updatedFieldsStep1,
        cards
      );

      setShowShippingPrice(true);

      cartDispatch({
        type: "reloadFields",
        payload: {
          updated_fields: updatedFieldsWithTerms,
          totals,
        },
      });
    } catch (error) {
      toast.error("Unexpected error while reloading fields");
    }
  }

  function submitNewAddress() {
    const newAddressInfo = {
      name: newAddress.name,
      street_1: newAddress.street_1,
      street_2: newAddress.street_2,
      state: newAddress.state,
      city: newAddress.city,
      zip_code: newAddress.zip_code,
      country_code: newAddress.country_code,
    };
    api.postRequest("orders/addAddress", newAddressInfo).then((response) => {
      if (response.isAxiosError) {
        const errorMessage = [];
        response.data.errors.forEach(function (error) {
          errorMessage.push(error);
        });
        toast.error(
          `There was an error processing your request. ${errorMessage.toString()}`
        );
      } else {
        cartDispatch({
          type: "updateFields",
          field: "ShipTo",
          payload: response.ShipToId,
        });
        setShipTo(newAddressInfo);
        setOpenNewAddressModal(false);
      }
    });
  }

  function submitNewCard() {
    const requiredFields = {};
    requiredFields["full_name"] = newCreditCard.Name;
    requiredFields["month"] = newCreditCard.ExpirationMonth;
    requiredFields["year"] = newCreditCard.ExpirationYear;
    window.Spreedly.tokenizeCreditCard(requiredFields);
  }

  async function handleVerifyAndAddPayment(data) {
    try {
      const response = await api.postRequest("credit/verify-credit", data);
      if (response.isAxiosError) {
        const errorMessage = "Please try again or another card";
        toast.error(
          `There was an error processing your request. ${errorMessage}`
        );
        return;
      }

      const res = response.data;

      if (res.response?.success) {
        toast.success(`${res.response.message}`);
        const payment = res.payment_method;
        const paymentInfo = {
          customerNo: customerNo,
          cardHolderName: payment.full_name,
          cardType: payment.card_type,
          cardLast4: payment.last_four_digits,
          expirationMMYY: functions.formatMMYY(payment.month, payment.year),
          cardToken: payment.token,
        };

        const addPaymentResponse = await api.postRequest("credit/add-payment", {
          payment: paymentInfo,
        });

        if (addPaymentResponse.isAxiosError) {
          const errorMessage =
            addPaymentResponse.data?.errors?.map((e) => e).join(", ") ||
            "Unknown error";
          toast.error(`Failed to add payment. ${errorMessage}`);
        } else {
          toast.success("Payment added successfully");
          setOpenNewCardModal(false);
        }
      }
    } catch (error) {
      toast.error("An unexpected error occurred.");
    }
  }

  async function handleGetPayments(data) {
    try {
      const response = await api.postRequest("credit/get-payment", data);
      const res = response.data;
      if (response.isAxiosError) {
        const errorMessage = "Failed to get payment: Unexpected response.";
        toast.error(
          `There was an error processing your request. ${errorMessage}`
        );
        return;
      } else {
        return res;
      }
    } catch (error) {
      if (error) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.title ||
          "Failed to get payment.";
        toast.error(`There was an error processing your request. ${message}`);
      } else {
        toast.error("An unexpected error occurred.");
      }
    }
  }

  function updateTermsCodeValues(formFields, cards) {
    return formFields.map((field) => {
      if (field.name === "TermsCode") {
        const cardOptions = cards.map((card) => ({
          label: card.cardNoMasked,
          value: `${card.cardNoMasked}|${card.expiration}`,
        }));

        return {
          ...field,
          values: [...cardOptions, ...(field.values || [])],
        };
      }

      return field;
    });
  }

  useEffect(() => {
    cartDispatch({ type: "setPageLoaded", payload: false });
    cartDispatch({
      type: "setCheckout",
      payload: {
        fields: [],
        order: null,
        totals: null,
      },
    });
    cartDispatch({
      type: "setOrderSubmitted",
      payload: {
        placedAt: "",
      },
    });

    let isMounted = true;

    const body = {
      cart_id: cartId,
      ShipTo: null,
      TermsCode: null,
      ShipVia: null,
    };
    api.postRequest("/orders/form", body).then(async (response) => {
      response.data.totals.trans = 0;
      const order = response.data.order;
      const totals = response.data.totals;
      const baseFields = response.data.fields;
      const shipToField = baseFields.find((field) => field.name === "ShipTo");
      const defaultShipToValue = shipToField?.default;

      if (isMounted) {
        setCustomerNo(response.data.customerNo);
      }
      const customerPayload = {
        customer: { customerNo: currentUser.CustomerNo },
      };

      try {
        const paymentCards = await handleGetPayments(customerPayload);
        const termsUpdatedFields = updateTermsCodeValues(
          baseFields,
          paymentCards
        );

        cartDispatch({
          type: "setCheckout",
          payload: {
            fields: sortFields(termsUpdatedFields),
            order,
            totals,
          },
        });
        cartDispatch({
          type: "updateFields",
          field: "ShipTo",
          payload: defaultShipToValue,
        });
        cartDispatch({ type: "setPageLoaded", payload: true });

        const gaCheckoutInfo = {
          items: cart,
          value: totals.total,
        };
        gaFunctions.gaBeginCheckout(gaCheckoutInfo);
      } catch (error) {
        toast.error("An unexpected error occurred during checkout.");
      }
    });
    return () => {
      isMounted = false; // cleanup: mark as unmounted
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (pageLoaded) {
      reloadFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipVia, openNewCardModal, shipTo]);

  useEffect(() => {
    setIsCheckoutValid(validateCheckoutFields());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  useEffect(() => {
    if (!openNewCardModal) return;
    const timeoutId = setTimeout(() => {
      window.Spreedly.init("7F62ATR3SX9K6SFJS9SE9FFBKV", {
        numberEl: "spreedly-number-test",
        cvvEl: "spreedly-cvv-test",
      });
      window.Spreedly.on("errors", function (errors) {
        console.error("Spreedly Errors:", errors);

        const errorMessages = errors
          .map((e) => `${e.attribute}: ${e.message}`)
          .join(", ");

        toast.error(`Payment validation failed: ${errorMessages}`);
      });
      window.Spreedly.on("paymentMethod", (token, pmData) => {
        cartDispatch({
          type: "setNewCreditCard",
          field: "PaymentToken",
          payload: pmData,
        });
        const data = {
          transaction: {
            payment_method_token: pmData.token,
            retain_on_success: true,
          },
        };
        window.Spreedly.on("errors", function (errors) {
          for (var i = 0; i < errors.length; i++) {
            var error = errors[i];
            console.log(error, i);
          }
        });
        handleVerifyAndAddPayment(data);
      });
    }, 300);

    window.Spreedly.on("ready", function () {
      window.Spreedly.setParam("allow_blank_name", false);
      window.Spreedly.setParam("allow_expired_date", false);
      window.Spreedly.setFieldType("number", "text");
      window.Spreedly.setFieldType("cvv", "text");
      window.Spreedly.setStyle(
        "number",
        "width: 67%;  border: 1px solid #ccc; padding: .65em .5em; font-size: 91%;"
      );
      window.Spreedly.setStyle(
        "cvv",
        "width: 30%;  border: 1px solid #ccc; padding: .65em .5em; font-size: 91%;"
      );
    });
    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNewCardModal]);

  return (
    <CartContext.Provider value={{ cartState, cartDispatch }}>
      <Container maxWidth="xl">
        <Mobile>
          <Stack spacing={2}>
            <Grid item xs={6}>
              <Card
                variant="outlined"
                sx={{
                  marginTop: "1.6rem",
                  padding: "2rem",
                }}
              >
                {!submitted ? (
                  <Stack spacing={2}>
                    <Typography variant="h1" sx={{ margin: "unset" }}>
                      Place Your Order
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      align="center"
                      sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                    ></Typography>
                    {fields &&
                      fields.map((field) => (
                        <React.Fragment key={field.name}>
                          {field.name === "ShipTo"
                            ? SelectField(field)
                            : field.name === "ShipVia"
                            ? ShipViaField(field)
                            : field.name === "MultipleOk"
                            ? SelectField(field)
                            : field.name === "TermsCode"
                            ? SelectField(field)
                            : field.name === "PoNumber"
                            ? InputField(field)
                            : field.name === "Notes"
                            ? SpecialInstructions(field)
                            : ""}
                        </React.Fragment>
                      ))}
                    {!pageLoaded && (
                      <Typography variant="subtitle2">* = required</Typography>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={3}>
                    <Typography variant="h1" sx={{ margin: "unset" }}>
                      Your Order Has Been Submitted!
                    </Typography>
                    <Typography>
                      Please check your email for confirmation.
                    </Typography>
                    <Typography>Order Placed At: {placedAt}</Typography>
                    <Typography>
                      Billing To: {currentUser.BillTo.Street1} |{" "}
                      {currentUser.BillTo.Street2} | {currentUser.BillTo.City},{" "}
                      {currentUser.BillTo.State} {currentUser.BillTo.ZipCode}
                    </Typography>
                    <Typography>Shipping To: {shippingAddress}</Typography>
                  </Stack>
                )}
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card
                variant="outlined"
                sx={{ marginTop: "1.6rem", padding: "2rem" }}
              >
                <Stack spacing={2}>
                  <Typography variant="h1" sx={{ margin: "unset" }}>
                    Order Details
                  </Typography>
                  <Typography
                    variant="greenRectangle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <InfoIcon /> Prices and weights shown are estimates and
                    subject to change. Most product is sold by weight and billed
                    at daily market prices. Orders will be priced at the metal
                    markets on the date of receipt, if order is shipped in the
                    same day; or the markets of the day after the order is
                    received for out of stock product. Market price is the
                    second London fix plus a surcharge.
                  </Typography>
                  <Typography
                    variant="articleSubTitle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <ReactMarkdown linkTarget="_blank">
                      If you currently pay by Credit Card, you will now see a
                      2.9% Credit Card fee on your invoice. If you would like to
                      avoid this fee, [**CLICK
                      HERE**](https://api2.hooverandstrong.com/pdfs/CreditApplication-2-7-24-Fillable.pdf)
                      to fill out a Net 30 account credit application.
                    </ReactMarkdown>
                  </Typography>
                  <Typography
                    variant="articleSubTitle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <ReactMarkdown linkTarget="_blank">
                      Please note certified diamonds and harmony melee
                      availability is not confirmed until your order is
                      processed and you receive a sales order confirmation
                    </ReactMarkdown>
                  </Typography>
                  <Stack spacing={3}>
                    {totals && (
                      <>
                        {cart.map((lineItem) => (
                          <React.Fragment key={lineItem.id}>
                            {lineItem.invoices.map((invoice) => (
                              <Stack key={invoice.specification}>
                                <Typography
                                  variant="articleText"
                                  sx={{ marginBottom: "1rem" }}
                                >
                                  <strong>
                                    {parse(invoice.specification)}
                                  </strong>{" "}
                                  | {formatting["removeSpace"](invoice.weight)}{" "}
                                  | {formatting["price2"](invoice.total)}
                                  {lineItem.material === "D" &&
                                  lineItem.finger_size !== "" ? (
                                    <>
                                      <br />
                                      SKU {lineItem.finger_size}
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </Typography>
                                <Typography variant="subtitle2">
                                  Estimated Ship Date:{" "}
                                  {lineItem.ship_info.ship_date}
                                </Typography>
                              </Stack>
                            ))}
                          </React.Fragment>
                        ))}

                        <Divider flexItem />
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Subtotal: {formatting["price2"](totals.subtotal)}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Shipping:
                          {showShippingPrice ? (
                            formatting["price2"](totals.trans)
                          ) : (
                            <Skeleton
                              variant="text"
                              width="10rem"
                              className="float-right"
                            />
                          )}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Sales Tax:
                          {showShippingPrice ? (
                            formatting["price2"](
                              totals.tax && totals.tax.amount
                            )
                          ) : (
                            <Skeleton
                              variant="text"
                              width="10rem"
                              className="float-right"
                            />
                          )}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          <strong>
                            Total:{" "}
                            {showShippingPrice ? (
                              formatting["price2"](totals.total)
                            ) : (
                              <Skeleton
                                variant="text"
                                width="10rem"
                                className="float-right"
                              />
                            )}
                          </strong>
                        </Typography>
                      </>
                    )}
                  </Stack>
                  {!submitted && (
                    <Stack
                      direction="row"
                      spacing={3}
                      justifyContent="flex-end"
                      alignItems="flex-end"
                      sx={{ marginTop: "2rem" }}
                    >
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        onClick={() => checkout()}
                        disabled={!isCheckoutValid}
                      >
                        Place Order
                      </Button>
                      <Button
                        size="small"
                        color="secondary"
                        variant="contained"
                      >
                        <DomLink to={{ pathname: "/cart" }}>Cancel</DomLink>
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Card>
            </Grid>
          </Stack>
        </Mobile>
        <Default>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Card
                variant="outlined"
                sx={{
                  marginTop: "1.6rem",
                  padding: "3rem",
                  minHeight: submitted ? "" : "100rem",
                }}
              >
                {!submitted ? (
                  <Stack spacing={2}>
                    <Typography variant="h1" sx={{ margin: "unset" }}>
                      Place Your Order
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      align="center"
                      sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                    ></Typography>
                    {!pageLoaded ? (
                      <Stack spacing={1} sx={{ width: "100%" }}>
                        <Skeleton animation="wave" height={40} />
                        <Skeleton animation="wave" height={40} />
                        <Skeleton animation="wave" height={40} />
                        <Skeleton animation="wave" height={40} />
                        <Skeleton animation="wave" height={40} />
                      </Stack>
                    ) : (
                      <>
                        {fields &&
                          fields.map((field) => (
                            <React.Fragment key={field.name}>
                              {field.name === "ShipTo"
                                ? SelectField(field)
                                : field.name === "ShipVia"
                                ? ShipViaField(field)
                                : field.name === "MultipleOk"
                                ? SelectField(field)
                                : field.name === "TermsCode"
                                ? SelectField(field)
                                : field.name === "PoNumber"
                                ? InputField(field)
                                : field.name === "Notes"
                                ? SpecialInstructions(field)
                                : null}
                            </React.Fragment>
                          ))}
                        <Typography variant="subtitle2">
                          * = required
                        </Typography>
                      </>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={3}>
                    <Typography variant="h1" sx={{ margin: "unset" }}>
                      Your Order Has Been Submitted!
                    </Typography>
                    <Typography>
                      Please check your email for confirmation.
                    </Typography>
                    <Typography>Order Placed At: {placedAt}</Typography>
                    <Typography>
                      Billing To: {currentUser.BillTo.Street1} |{" "}
                      {currentUser.BillTo.Street2} | {currentUser.BillTo.City},{" "}
                      {currentUser.BillTo.State} {currentUser.BillTo.ZipCode}
                    </Typography>
                    <Typography>Shipping To: {shippingAddress}</Typography>
                  </Stack>
                )}
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card
                variant="outlined"
                sx={{ marginTop: "1.6rem", padding: "3rem" }}
              >
                <Stack spacing={2}>
                  <Typography variant="h1" sx={{ margin: "unset" }}>
                    Order Details
                  </Typography>
                  <Typography
                    variant="greenRectangle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <InfoIcon /> Prices and weights shown are estimates and
                    subject to change. Most product is sold by weight and billed
                    at daily market prices. Orders will be priced at the metal
                    markets on the date of receipt, if order is shipped in the
                    same day; or the markets of the day after the order is
                    received for out of stock product. Market price is the
                    second London fix plus a surcharge.
                  </Typography>
                  <Typography
                    variant="articleSubTitle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <ReactMarkdown linkTarget="_blank">
                      If you currently pay by Credit Card, you will now see a
                      2.9% Credit Card fee on your invoice. If you would like to
                      avoid this fee, [**CLICK
                      HERE**](https://api2.hooverandstrong.com/pdfs/CreditApplication-2-7-24-Fillable.pdf)
                      to fill out a Net 30 account credit application.
                    </ReactMarkdown>
                  </Typography>
                  <Typography
                    variant="articleSubTitle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <ReactMarkdown linkTarget="_blank">
                      Please note certified diamonds and harmony melee
                      availability is not confirmed until your order is
                      processed and you receive a sales order confirmation
                    </ReactMarkdown>
                  </Typography>
                  <Stack
                    spacing={3}
                    justifyContent="flex-end"
                    alignItems="flex-end"
                  >
                    {totals && (
                      <>
                        {cart.map((lineItem) => (
                          <React.Fragment key={lineItem.id}>
                            {lineItem.invoices.map((invoice) => (
                              <Stack
                                key={invoice.specification}
                                justifyContent="flex-end"
                                alignItems="flex-end"
                              >
                                <Typography
                                  variant="articleText"
                                  sx={{ marginBottom: "1rem" }}
                                >
                                  {parse(invoice.specification)} |{" "}
                                  {formatting["removeSpace"](invoice.weight)} |{" "}
                                  {formatting["price2"](invoice.total)}
                                  {lineItem.material === "D" &&
                                  lineItem.finger_size !== "" ? (
                                    <>
                                      <br />
                                      SKU {lineItem.finger_size}
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </Typography>
                                <Typography variant="subtitle2">
                                  Estimated Ship Date:{" "}
                                  {lineItem.ship_info.ship_date}
                                </Typography>
                              </Stack>
                            ))}
                          </React.Fragment>
                        ))}

                        <Divider flexItem />
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Subtotal: {formatting["price2"](totals.subtotal)}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Shipping:{" "}
                          {showShippingPrice ? (
                            formatting["price2"](totals.trans)
                          ) : (
                            <Skeleton
                              variant="text"
                              width="10rem"
                              className="float-right"
                            />
                          )}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Sales Tax:{" "}
                          {showShippingPrice ? (
                            formatting["price2"](
                              totals.tax && totals.tax.amount
                            )
                          ) : (
                            <Skeleton
                              variant="text"
                              width="10rem"
                              className="float-right"
                            />
                          )}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Total:{" "}
                          {showShippingPrice ? (
                            formatting["price2"](totals.total)
                          ) : (
                            <Skeleton
                              variant="text"
                              width="10rem"
                              className="float-right"
                            />
                          )}
                        </Typography>
                      </>
                    )}
                  </Stack>
                  {!submitted && (
                    <Stack
                      direction="row"
                      spacing={3}
                      justifyContent="flex-end"
                      alignItems="flex-end"
                      sx={{ marginTop: "2rem" }}
                    >
                      <DomLink to={{ pathname: "/cart" }}>
                        <Button
                          size="small"
                          color="secondary"
                          variant="contained"
                        >
                          Cancel
                        </Button>
                      </DomLink>
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        onClick={() => checkout()}
                        disabled={!isCheckoutValid}
                      >
                        Place Order
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Default>
        <Dialog
          open={openNewAddressModal}
          onClose={() => setOpenNewAddressModal(false)}
          fullWidth
          scroll="body"
        >
          <>
            <DialogTitle sx={{ textTransform: "lowercase" }}>
              New Shipping Address
            </DialogTitle>
            <DialogContent sx={{ minHeight: "40rem" }} dividers={true}>
              <Stack spacing={1}>
                <Typography sx={{ marginBottom: "2rem" }}>
                  Fill out the fields to add a new address.
                </Typography>
                <InputLabel>Name *</InputLabel>
                <TextField
                  color="primary"
                  onChange={(e) =>
                    cartDispatch({
                      type: "setNewAddress",
                      field: "name",
                      payload: e.target.value,
                    })
                  }
                  value={newAddress.name}
                  size="small"
                  sx={{
                    width: "100%",
                    margin: "unset",
                  }}
                />
                <InputLabel>Street 1 *</InputLabel>
                <TextField
                  color="primary"
                  onChange={(e) =>
                    cartDispatch({
                      type: "setNewAddress",
                      field: "street_1",
                      payload: e.target.value,
                    })
                  }
                  value={newAddress.street_1}
                  size="small"
                  sx={{
                    width: "100%",
                    margin: "unset",
                  }}
                />
                <InputLabel>Street 2</InputLabel>
                <TextField
                  color="primary"
                  onChange={(e) =>
                    cartDispatch({
                      type: "setNewAddress",
                      field: "street_2",
                      payload: e.target.value,
                    })
                  }
                  value={newAddress.street_2}
                  size="small"
                  sx={{
                    width: "100%",
                    margin: "unset",
                  }}
                />
                <Stack direction={"row"} gap={2}>
                  <Box width={"100%"}>
                    <InputLabel>State/Province *</InputLabel>
                    <TextField
                      color="primary"
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewAddress",
                          field: "state",
                          payload: e.target.value,
                        })
                      }
                      value={newAddress.state}
                      size="small"
                      sx={{
                        width: "100%",
                        margin: "unset",
                      }}
                    />
                  </Box>
                  <Box width={"100%"}>
                    <InputLabel>Country *</InputLabel>
                    <Select
                      value={newAddress.country_code}
                      size="small"
                      onChange={(e) => {
                        cartDispatch({
                          type: "setNewAddress",
                          field: "country_code",
                          payload: e.target.value,
                        });
                      }}
                      sx={{ width: "100%" }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            border: "1px solid #939598",
                            borderRadius: "0",
                            overflowY: "auto",
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      {constants.countriesOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Stack>
                <Stack direction={"row"} gap={2}>
                  <Box width={"100%"}>
                    <InputLabel>City *</InputLabel>
                    <TextField
                      color="primary"
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewAddress",
                          field: "city",
                          payload: e.target.value,
                        })
                      }
                      value={newAddress.city}
                      size="small"
                      sx={{
                        width: "100%",
                        margin: "unset",
                      }}
                    />
                  </Box>
                  <Box width={"100%"}>
                    <InputLabel>Zip Code *</InputLabel>
                    <TextField
                      color="primary"
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewAddress",
                          field: "zip_code",
                          payload: e.target.value,
                        })
                      }
                      value={newAddress.zip_code}
                      size="small"
                      sx={{
                        width: "100%",
                        margin: "unset",
                      }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Stack direction="row" gap={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setOpenNewAddressModal(false)}
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => submitNewAddress()}
                  variant="contained"
                  color="success"
                  size="small"
                >
                  Add
                </Button>
              </Stack>
            </DialogActions>
          </>
        </Dialog>
        <Dialog
          open={openNewCardModal}
          onClose={() => setOpenNewCardModal(false)}
          maxWidth="sm"
        >
          <>
            <DialogTitle
              sx={{ textTransform: "capitalize", fontWeight: "bold" }}
            >
              Add New Credit Card
            </DialogTitle>
            <DialogContent dividers>
              <Grid container gap={2}>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    Fill out the fields below to add a new credit card.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel htmlFor="name" shrink>
                    Name *
                  </InputLabel>
                  <Box
                    id="name"
                    component="input"
                    type="text"
                    value={newCreditCard.Name}
                    onChange={(e) => handleTermsChange(e)}
                    sx={inputBoxStyles}
                  />
                </Grid>
                <Grid container>
                  <Grid item xs={8}>
                    <InputLabel
                      htmlFor="spreedly-number-test"
                      sx={inputLabelStyles}
                    >
                      Card Number *
                    </InputLabel>
                    <Box id="spreedly-number-test" sx={iFrameBoxStyles} />
                  </Grid>
                  <Grid item xs={3}>
                    <InputLabel
                      htmlFor="spreedly-cvv-test"
                      sx={inputLabelStyles}
                    >
                      CVV *
                    </InputLabel>
                    <Box id="spreedly-cvv-test" sx={iFrameBoxStyles} />
                  </Grid>
                </Grid>
                <Grid container gap={2}>
                  <Grid item xs={6} sm={4}>
                    <InputLabel htmlFor="exp-month" shrink>
                      Exp Month (MM) *
                    </InputLabel>
                    <Box
                      id="exp-month"
                      component="input"
                      type="text"
                      value={newCreditCard.ExpirationMonth}
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewCreditCard",
                          field: "ExpirationMonth",
                          payload: e.target.value,
                        })
                      }
                      sx={inputBoxStyles}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} sx={{ marginBottom: "2rem" }}>
                    <InputLabel htmlFor="exp-year" shrink>
                      Exp Year (YYYY) *
                    </InputLabel>
                    <Box
                      id="exp-year"
                      component="input"
                      type="text"
                      value={newCreditCard.ExpirationYear}
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewCreditCard",
                          field: "ExpirationYear",
                          payload: e.target.value,
                        })
                      }
                      sx={inputBoxStyles}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Stack direction="row" gap={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setOpenNewCardModal(false)}
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => submitNewCard()}
                  variant="contained"
                  color="success"
                  size="small"
                >
                  Add
                </Button>
              </Stack>
            </DialogActions>
          </>
        </Dialog>
        {!pageLoaded && (
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={true}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </Container>
    </CartContext.Provider>
  );
}
